import { useEffect, useState } from 'react';
import { api, ApiClientError } from './api';

export interface ApiSnapshot {
  sym: string;
  market: 'REG' | 'ODL';
  asOf: string;
  source: string;
  last?: number;
  bid?: number;
  ask?: number;
  spread?: number;
  open?: number;
  high?: number;
  low?: number;
  volume?: number;
  trades?: number;
  vwap?: number;
}

export interface ApiTick {
  time: string;
  price: number;
  volume: number;
  tsMs: number;
}

const isFatal = (err: unknown): boolean => {
  // Network failures, CORS, 404s — keep stale data and let the UI fall back.
  // Re-throw genuinely unexpected errors so they bubble to the error boundary.
  if (err instanceof ApiClientError) return false;
  if (err instanceof TypeError) return false; // fetch network failure
  return true;
};

/** Single-symbol live snapshot. Returns null until the first successful fetch. */
export const useStockSnapshot = (sym: string | null, intervalMs = 5000): ApiSnapshot | null => {
  const [data, setData] = useState<ApiSnapshot | null>(null);
  useEffect(() => {
    if (!sym) return;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const snap = await api.get<ApiSnapshot>(`/stocks/${sym}`);
        if (!cancelled) setData(snap);
      } catch (err) {
        if (isFatal(err)) throw err;
        // keep stale
      }
    };
    void fetchOnce();
    const id = setInterval(fetchOnce, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [sym, intervalMs]);
  return data;
};

/** Batch snapshots for a list of symbols. Map keyed by sym. */
export const useStockSnapshots = (
  syms: string[],
  intervalMs = 5000,
): Map<string, ApiSnapshot> => {
  const [data, setData] = useState<Map<string, ApiSnapshot>>(() => new Map());
  const key = syms.join(',');
  useEffect(() => {
    if (!syms.length) return;
    let cancelled = false;
    const fetchAll = async () => {
      const results = await Promise.allSettled(
        syms.map((s) => api.get<ApiSnapshot>(`/stocks/${s}`)),
      );
      if (cancelled) return;
      // Merge successful into existing map so a single failure doesn't blank the row.
      setData((prev) => {
        const next = new Map(prev);
        results.forEach((r, i) => {
          if (r.status === 'fulfilled') next.set(syms[i], r.value);
        });
        return next;
      });
    };
    void fetchAll();
    const id = setInterval(fetchAll, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, intervalMs]);
  return data;
};

export interface StockOverview {
  sym: string;
  name: string | null;
  sector: string | null;
  market: string;
  prevClose: number | null;
  priorClose: number | null;
  lastPrice: number | null;
  yearHigh: number | null;
  yearLow: number | null;
  avgVol30d: number | null;
  asOf: string;
}

/** Slow-changing per-symbol metadata. Fetched once on mount + every 6h. */
export const useStockOverview = (sym: string | null, intervalMs = 6 * 3600 * 1000): StockOverview | null => {
  const [data, setData] = useState<StockOverview | null>(null);
  useEffect(() => {
    if (!sym) return;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await api.get<StockOverview>(`/stocks/${sym}/overview`);
        if (!cancelled) setData(r);
      } catch (err) {
        if (isFatal(err)) throw err;
      }
    };
    void fetchOnce();
    const id = setInterval(fetchOnce, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [sym, intervalMs]);
  return data;
};

export interface ApiCandle {
  tsMs: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export type CandleRange = '1D' | '1W' | '1M' | '3M' | '1Y' | '5Y';

/** OHLCV candles for the chart. Polls every minute on 1D, hourly on longer
 *  ranges — bar resolution dictates how often the API can produce a new point. */
export const useStockCandles = (
  sym: string | null,
  range: CandleRange,
): { candles: ApiCandle[] | null; interval: '1m' | '1d' | null; loading: boolean } => {
  const [candles, setCandles] = useState<ApiCandle[] | null>(null);
  const [interval, setIntv] = useState<'1m' | '1d' | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!sym) return;
    let cancelled = false;
    const refreshMs = range === '1D' ? 60_000 : 60 * 60_000;
    const fetchOnce = async () => {
      setLoading(true);
      try {
        const r = await api.get<{ candles: ApiCandle[]; interval: '1m' | '1d' }>(
          `/stocks/${sym}/candles?range=${range}`,
        );
        if (!cancelled) {
          setCandles(r.candles);
          setIntv(r.interval);
        }
      } catch (err) {
        if (isFatal(err)) throw err;
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchOnce();
    const id = setInterval(fetchOnce, refreshMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [sym, range]);
  return { candles, interval, loading };
};

/** Trade tape for one symbol. */
export const useStockTicks = (
  sym: string | null,
  limit = 300,
  intervalMs = 5000,
): ApiTick[] | null => {
  const [data, setData] = useState<ApiTick[] | null>(null);
  useEffect(() => {
    if (!sym) return;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const res = await api.get<{ ticks: ApiTick[] }>(`/stocks/${sym}/ticks?limit=${limit}`);
        if (!cancelled) setData(res.ticks);
      } catch (err) {
        if (isFatal(err)) throw err;
      }
    };
    void fetchOnce();
    const id = setInterval(fetchOnce, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [sym, limit, intervalMs]);
  return data;
};
